/**
 * UnfcccEmailList — UNFCCC E-posta Listesi Yönetimi
 * - Ülke dropdown (her ülkeden sadece 1 email)
 * - Robust token kontrolü (boş token gönderilmez)
 * - Silme: Ultra Password ile onay
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { apiClient, getToken } from '../lib/apiClient';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Upload, Mail, X, AlertCircle, CheckCircle2, Eye, EyeOff, Globe, Lock, Send, RefreshCw, Clock } from 'lucide-react';
import { COUNTRIES as CANONICAL_COUNTRIES } from '../countries';

/* ── Rozet ─────────────────────────────────────────────────────────── */
function Badge({ n }) {
  return (
    <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-500 rounded-full">{n}</span>
  );
}

/* ── Invitation Status Badge ─────────────────────────────────────────── */
function InvitationBadge({ status, tr }) {
  const configs = {
    not_sent: { bg: 'bg-gray-100', text: 'text-gray-600', label: tr ? 'Davet Yok' : 'Not Sent', icon: null },
    sent: { bg: 'bg-blue-100', text: 'text-blue-700', label: tr ? 'Gönderildi' : 'Sent', icon: Send },
    expired: { bg: 'bg-amber-100', text: 'text-amber-700', label: tr ? 'Süresi Doldu' : 'Expired', icon: Clock },
    completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: tr ? 'Tamamlandı' : 'Completed', icon: CheckCircle2 },
    failed: { bg: 'bg-red-100', text: 'text-red-700', label: tr ? 'Hata' : 'Failed', icon: AlertCircle },
  };
  
  const config = configs[status] || configs.not_sent;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold ${config.bg} ${config.text} border border-current border-opacity-20`}>
      {Icon && <Icon className="h-2.5 w-2.5" />}
      {config.label}
    </span>
  );
}

/* ── Silme Onay Modalı ─────────────────────────────────────────────── */
function DeleteModal({ email, emailId, onCancel, onDeleted, tr }) {
  const [pw, setPw]           = useState('');
  const [show, setShow]       = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef              = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const confirm = async () => {
    if (!pw.trim()) { setError(tr ? 'Şifrenizi girin.' : 'Enter your password.'); return; }
    setLoading(true); setError('');
    try {
      const t = getToken();
      if (!t) { setError(tr ? 'Oturum süresi dolmuş, yeniden giriş yapın.' : 'Session expired, please re-login.'); return; }
      await apiClient.delete(`/ultra-admin/unfccc-emails/${emailId}`, { data: { password: pw } });
      toast.success(tr ? 'E-posta silindi.' : 'Email deleted.');
      onDeleted(emailId);
    } catch (err) {
      setError(err?.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
              <Trash2 className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{tr ? 'Silme Onayı' : 'Confirm Delete'}</p>
              <p className="text-xs text-gray-400 truncate max-w-[190px]">{email}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-gray-300 hover:text-gray-600 transition-colors p-1"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {tr ? 'Bu e-postayı listeden kaldırmak için Ultra Password giriniz.' : 'Enter the Ultra Password to remove this email from the list.'}
        </p>
        <div className="relative">
          <input
            ref={inputRef}
            type={show ? 'text' : 'password'}
            placeholder="Ultra Password"
            value={pw}
            onChange={e => { setPw(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && confirm()}
            className="w-full h-10 px-3 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <button type="button" onClick={() => setShow(s => !s)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {error && (
          <div className="flex items-center gap-1.5 text-xs text-red-500 mt-1.5"><AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />{error}</div>
        )}
        <div className="flex gap-2 mt-4">
          <button onClick={onCancel} className="flex-1 h-9 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium">
            {tr ? 'İptal' : 'Cancel'}
          </button>
          <button onClick={confirm} disabled={loading || !pw.trim()}
            className="flex-1 h-9 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors">
            {loading ? '...' : (tr ? 'Sil' : 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Düzenleme Modalı (Phase 86AU) ─────────────────────────────────── */
function EditModal({ entry, countries, onCancel, onSaved, tr }) {
  const [email, setEmail]       = useState(entry.email || '');
  const [name, setName]         = useState(entry.name || '');
  const [country, setCountry]   = useState(entry.country || '');
  const [countryCode, setCC]    = useState(entry.country_code || '');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  
  // Find display name for current country (for dropdown value)
  const displayCountry = useMemo(() => {
    if (!country) return '';
    const found = countries.find(c => c.en === country || c.tr === country || c.code === countryCode);
    return found ? (tr ? found.tr : found.en) : country;
  }, [country, countryCode, countries, tr]);

  const save = async () => {
    if (!email.trim()) { setError(tr ? 'E-posta zorunlu.' : 'Email is required.'); return; }
    setLoading(true); setError('');
    try {
      const r = await apiClient.patch(`/ultra-admin/unfccc-emails/${entry.id}`, {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        country: country.trim(),
        country_code: countryCode.trim().toUpperCase(),
      });
      toast.success(tr ? 'Kayıt güncellendi.' : 'Entry updated.');
      onSaved(r.data?.entry || { ...entry, email: email.trim().toLowerCase(), name: name.trim(), country: country.trim(), country_code: countryCode.trim().toUpperCase() });
    } catch (err) {
      setError(err?.response?.data?.detail || (tr ? 'Bir hata oluştu.' : 'An error occurred.'));
    } finally { setLoading(false); }
  };

  const onCountryChange = (e) => {
    const selectedName = e.target.value;
    // Find country by display name (tr or en)
    const sel = countries.find(c => (tr ? c.tr : c.en) === selectedName);
    if (sel) {
      setCountry(sel.en); // Store English name in DB
      setCC(sel.code);
    } else {
      setCountry(selectedName);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
         onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" data-testid="unfccc-edit-modal">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Pencil className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{tr ? 'UNFCCC Kaydını Düzenle' : 'Edit UNFCCC Entry'}</p>
              <p className="text-xs text-gray-400">{tr ? 'Sadece profil oluşturmamış kayıtlar' : 'Only entries without a profile'}</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-gray-300 hover:text-gray-600 transition-colors p-1"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Ad Soyad' : 'Full Name'}</label>
            <input data-testid="unfccc-edit-name" value={name} onChange={e => { setName(e.target.value); setError(''); }}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'E-posta' : 'Email'}</label>
            <input data-testid="unfccc-edit-email" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(''); }}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">{tr ? 'Ülke' : 'Country'}</label>
            <select data-testid="unfccc-edit-country" value={displayCountry} onChange={onCountryChange}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
              <option value="">{tr ? 'Seçin' : 'Select'}</option>
              {countries.map(c => (
                <option key={c.code} value={tr ? c.tr : c.en}>
                  {tr ? c.tr : c.en}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && (
          <div className="flex items-center gap-1.5 text-xs text-red-500 mt-3"><AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />{error}</div>
        )}
        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="flex-1 h-10 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium">
            {tr ? 'İptal' : 'Cancel'}
          </button>
          <button data-testid="unfccc-edit-save" onClick={save} disabled={loading || !email.trim()}
            className="flex-1 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors">
            {loading ? '...' : (tr ? 'Kaydet' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Ana bileşen ───────────────────────────────────────────────────── */
export default function UnfcccEmailList({ tr }) {
  const [emails,        setEmails]        = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [authError,     setAuthError]     = useState(false); // token eksik?
  const [search,        setSearch]        = useState('');

  /* Ekleme formu */
  const [newEmail,       setNewEmail]       = useState('');
  const [newName,        setNewName]        = useState('');
  const [newCountryCode, setNewCountryCode] = useState('');
  const [addErr,         setAddErr]         = useState('');
  const [adding,         setAdding]         = useState(false);

  /* Country list localized + sorted for current language */
  const localizedCountries = useMemo(() => {
    const key = tr ? 'tr' : 'en';
    return [...CANONICAL_COUNTRIES].sort((a, b) =>
      a[key].localeCompare(b[key], tr ? 'tr-TR' : 'en-US'),
    );
  }, [tr]);

  /* Helper: render a country in the current language (fallback legacy free-text) */
  const displayCountry = useCallback((entry) => {
    if (!entry) return '';
    if (entry.country_code) {
      const found = CANONICAL_COUNTRIES.find(c => c.code === entry.country_code);
      if (found) return tr ? found.tr : found.en;
    }
    // Legacy row — attempt reverse match by EN or TR name
    if (entry.country) {
      const found = CANONICAL_COUNTRIES.find(
        c => c.en === entry.country || c.tr === entry.country,
      );
      if (found) return tr ? found.tr : found.en;
      return entry.country;
    }
    return '';
  }, [tr]);

  /* Import */
  const [importing,    setImporting]    = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  /* Silme modalı */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);   // Phase 86AU
  
  /* Invitation state */
  const [sendingInvite, setSendingInvite] = useState({});
  
  /* Email Preview Modal */
  const [previewModal, setPreviewModal] = useState(null); // { email_id, email, country, name }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');

  /* ── Preview Email ── */
  const handlePreviewEmail = async (emailId, email, country, name) => {
    setPreviewModal({ email_id: emailId, email, country, name });
    setPreviewLoading(true);
    try {
      const r = await apiClient.post(`/ultra-admin/unfccc-invitations/preview/${emailId}`);
      setEmailPreviewHtml(r.data.html_preview || '');
    } catch (e) {
      const msg = e?.response?.data?.detail || (tr ? 'Önizleme yüklenemedi.' : 'Failed to load preview.');
      toast.error(msg);
      setPreviewModal(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  /* ── Confirm and Send from Preview ── */
  const handleConfirmSend = async () => {
    if (!previewModal) return;
    const { email_id, email } = previewModal;
    setPreviewModal(null);
    await handleSendInvitation(email_id, email);
  };

  /* ── Send Invitation ── */
  const handleSendInvitation = async (emailId, email) => {
    setSendingInvite(prev => ({ ...prev, [emailId]: true }));
    try {
      await apiClient.post(`/ultra-admin/unfccc-invitations/send/${emailId}`);
      toast.success(tr ? `Davet gönderildi: ${email}` : `Invitation sent to ${email}`);
      await load(); // Reload to update status
    } catch (e) {
      const msg = e?.response?.data?.detail || (tr ? 'Davet gönderilemedi.' : 'Failed to send invitation.');
      toast.error(msg);
    } finally {
      setSendingInvite(prev => ({ ...prev, [emailId]: false }));
    }
  };

  /* ── Resend Invitation ── */
  const handleResendInvitation = async (emailId, email) => {
    setSendingInvite(prev => ({ ...prev, [emailId]: true }));
    try {
      await apiClient.post(`/ultra-admin/unfccc-invitations/resend/${emailId}`);
      toast.success(tr ? `Davet yeniden gönderildi: ${email}` : `Invitation resent to ${email}`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || (tr ? 'Davet gönderilemedi.' : 'Failed to resend invitation.');
      toast.error(msg);
    } finally {
      setSendingInvite(prev => ({ ...prev, [emailId]: false }));
    }
  };

  /* ── Delete Invitation ── */
  const handleDeleteInvitation = async (emailId) => {
    try {
      await apiClient.delete(`/ultra-admin/unfccc-invitations/${emailId}`);
      toast.success(tr ? 'Davet silindi.' : 'Invitation deleted.');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || (tr ? 'Davet silinemedi.' : 'Failed to delete invitation.');
      toast.error(msg);
    }
  };

  /* ── Yükle ── */
  const load = useCallback(async () => {
    const t = getToken();
    if (!t) { setAuthError(true); return; }
    setAuthError(false);
    setLoading(true);
    try {
      const r = await apiClient.get('/ultra-admin/unfccc-emails');
      setEmails(r.data || []);
    } catch (e) {
      if (e?.response?.status === 401) setAuthError(true);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* ── Tek ekle ── */
  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { setAddErr(tr ? 'Geçerli bir e-posta girin.' : 'Enter a valid email.'); return; }
    if (!newName.trim()) { setAddErr(tr ? 'Ad Soyad zorunludur.' : 'Full name is required.'); return; }
    if (!newCountryCode) { setAddErr(tr ? 'Ülke seçimi zorunludur.' : 'Country is required.'); return; }

    const t = getToken();
    if (!t) { setAuthError(true); return; }

    const countryEntry = CANONICAL_COUNTRIES.find(c => c.code === newCountryCode);

    setAdding(true); setAddErr('');
    try {
      const r = await apiClient.post(
        '/ultra-admin/unfccc-emails',
        {
          email,
          name:         newName.trim(),
          country_code: newCountryCode,
          country:      countryEntry?.en || '',
        },
      );
      setEmails(prev => [r.data, ...prev]);
      setNewEmail(''); setNewName(''); setNewCountryCode('');
      toast.success(tr ? 'E-posta eklendi.' : 'Email added.');
    } catch (e) {
      if (e?.response?.status === 401) { setAuthError(true); return; }
      setAddErr(e?.response?.data?.detail || (tr ? 'Eklenemedi.' : 'Could not add.'));
    } finally { setAdding(false); }
  };

  /* ── CSV import ── */
  const handleImport = async file => {
    if (!file) return;
    const t = getToken();
    if (!t) { setAuthError(true); return; }
    setImporting(true); setImportResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await apiClient.post(
        '/ultra-admin/unfccc-emails/import',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(r.data);
      await load();

      const s = r.data.summary || {};
      const parts = [];
      if (s.added)              parts.push(tr ? `${s.added} eklendi`             : `${s.added} added`);
      if (s.duplicate_email)    parts.push(tr ? `${s.duplicate_email} zaten var`  : `${s.duplicate_email} duplicate`);
      if (s.country_duplicate)  parts.push(tr ? `${s.country_duplicate} ülke dolu`: `${s.country_duplicate} country locked`);
      if (s.invalid)            parts.push(tr ? `${s.invalid} geçersiz`           : `${s.invalid} invalid`);
      const msg = parts.length ? parts.join(' · ') : (tr ? 'Hiç satır işlenmedi.' : 'No rows processed.');
      if (s.added) toast.success(msg);
      else         toast.error(msg);
    } catch (e) {
      if (e?.response?.status === 401) { setAuthError(true); return; }
      toast.error(e?.response?.data?.detail || (tr ? 'Import başarısız.' : 'Import failed.'));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /* ── Download example template ── */
  const handleDownloadTemplate = async () => {
    const t = getToken();
    if (!t) { setAuthError(true); return; }
    try {
      const r = await apiClient.get('/ultra-admin/unfccc-emails/template.csv', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'unfccc-delegates-template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e?.response?.status === 401) { setAuthError(true); return; }
      toast.error(tr ? 'Şablon indirilemedi.' : 'Could not download template.');
    }
  };

  const onDeleted = id => { setEmails(prev => prev.filter(e => e.id !== id)); setDeleteTarget(null); };
  // Phase 86AU — Apply server-confirmed edits in place.
  const onEdited = (updated) => {
    if (!updated) { setEditTarget(null); return; }
    setEmails(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
    setEditTarget(null);
  };

  const q        = search.toLowerCase();
  const filtered = emails.filter(e =>
    !q ||
    e.email.toLowerCase().includes(q) ||
    (e.name || '').toLowerCase().includes(q) ||
    (e.country || '').toLowerCase().includes(q) ||
    (e.country_code || '').toLowerCase().includes(q) ||
    displayCountry(e).toLowerCase().includes(q)
  );

  /* ── Auth hata ekranı ── */
  if (authError) {
    return (
      <div className="max-w-xl">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex flex-col items-center gap-3 text-center">
          <AlertCircle className="h-8 w-8 text-amber-500" />
          <p className="font-semibold text-amber-800">
            {tr ? 'Oturum Süresi Doldu' : 'Session Expired'}
          </p>
          <p className="text-sm text-amber-600">
            {tr ? 'Lütfen sayfayı yenileyin veya yeniden giriş yapın.' : 'Please refresh the page or log in again.'}
          </p>
          <button onClick={() => { setAuthError(false); load(); }}
            className="px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors">
            {tr ? 'Yeniden Dene' : 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  /* ─────────────────────────── RENDER ─────────────────────────────── */
  return (
    <>
      {deleteTarget && (
        <DeleteModal email={deleteTarget.email} emailId={deleteTarget.id}
          onCancel={() => setDeleteTarget(null)} onDeleted={onDeleted} tr={tr} />
      )}

      {editTarget && (
        <EditModal entry={editTarget} countries={localizedCountries}
          onCancel={() => setEditTarget(null)} onSaved={onEdited} tr={tr} />
      )}

      <div className="max-w-3xl space-y-6">
        {/* Başlık */}
        <div>
          <h2 className="text-xl font-bold text-gray-900">{tr ? 'UNFCCC E-posta Listesi' : 'UNFCCC Email List'}</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {tr ? 'Blue Zone kayıt sayfasında doğrulanacak izinli e-postalar — her ülkeden 1 kayıt' : 'Allowed emails for Blue Zone registration — 1 entry per country'}
          </p>
        </div>

        {/* ── Tek Ekleme ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-sm font-semibold text-gray-700 mb-3">{tr ? 'Tek E-posta Ekle' : 'Add Single Email'}</p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {/* Email */}
            <input
              type="email"
              placeholder="ornek@kurum.org"
              value={newEmail}
              onChange={e => { setNewEmail(e.target.value); setAddErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              className="h-9 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            {/* Ad Soyad */}
            <input
              type="text"
              placeholder={tr ? 'Ad Soyad *' : 'Full Name *'}
              value={newName}
              onChange={e => { setNewName(e.target.value); setAddErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
              className="h-9 px-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
            {/* Ülke dropdown */}
            <div className="relative sm:col-span-2">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <select
                value={newCountryCode}
                onChange={e => { setNewCountryCode(e.target.value); setAddErr(''); }}
                className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white appearance-none"
              >
                <option value="">{tr ? 'Ülke Seçin *' : 'Select Country *'}</option>
                {localizedCountries.map(c => (
                  <option key={c.code} value={c.code}>{tr ? c.tr : c.en}</option>
                ))}
              </select>
            </div>
            {/* Ekle butonu */}
            <div className="sm:col-span-2 flex justify-end">
              <button
                onClick={handleAdd}
                disabled={adding}
                className="h-9 px-5 rounded-xl bg-gray-900 hover:bg-gray-700 disabled:opacity-40 text-white text-sm font-semibold flex items-center gap-2 transition-colors"
              >
                <Plus className="h-4 w-4" />
                {adding ? '...' : (tr ? 'Ekle' : 'Add')}
              </button>
            </div>
          </div>
          {addErr && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
              <AlertCircle className="h-3.5 w-3.5" />{addErr}
            </p>
          )}
        </div>

        {/* ── CSV Import ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between gap-4 mb-1">
            <div>
              <p className="text-sm font-semibold text-gray-700">
                {tr ? 'CSV ile Toplu Aktar' : 'Bulk Import from CSV'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {tr
                  ? 'Başlık: email,name,country_code — her ülkeden 1 kayıt.'
                  : 'Header: email,name,country_code — 1 entry per country.'}
              </p>
            </div>
            <button
              data-testid="unfccc-download-template"
              onClick={handleDownloadTemplate}
              className="h-8 px-3 rounded-xl border border-gray-200 hover:border-gray-400 text-gray-600 text-xs font-medium flex items-center gap-1.5 transition-colors flex-shrink-0"
            >
              <Globe className="h-3.5 w-3.5" />
              {tr ? 'Örnek CSV indir' : 'Download template'}
            </button>
          </div>

          <input
            type="file"
            accept=".csv,text/csv"
            ref={fileRef}
            onChange={e => e.target.files[0] && handleImport(e.target.files[0])}
            className="hidden"
          />
          <button
            data-testid="unfccc-upload-csv"
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className="h-9 px-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-gray-500 text-gray-600 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-40"
          >
            <Upload className="h-4 w-4" />
            {importing ? (tr ? 'Aktarılıyor...' : 'Importing...') : (tr ? '.csv Yükle' : 'Upload .csv')}
          </button>

          {importResult && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-xs">
                <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span className="text-green-700 font-medium">
                  {importResult.summary.added} {tr ? 'eklendi' : 'added'}
                </span>
                {importResult.summary.duplicate_email > 0 && (
                  <span className="text-gray-400">
                    · {importResult.summary.duplicate_email} {tr ? 'zaten var' : 'duplicate'}
                  </span>
                )}
                {importResult.summary.country_duplicate > 0 && (
                  <span className="text-amber-600">
                    · {importResult.summary.country_duplicate} {tr ? 'ülke kilitli' : 'country locked'}
                  </span>
                )}
                {importResult.summary.invalid > 0 && (
                  <span className="text-red-500">
                    · {importResult.summary.invalid} {tr ? 'geçersiz' : 'invalid'}
                  </span>
                )}
              </div>

              {/* Compact issue detail — first 5 per bucket */}
              {(importResult.invalid?.length || importResult.country_duplicate?.length) ? (
                <div className="text-[11px] text-gray-500 bg-gray-50 rounded-xl p-3 space-y-1.5 max-h-48 overflow-y-auto">
                  {importResult.invalid?.slice(0, 5).map((r, i) => (
                    <div key={`inv-${i}`} className="flex items-center gap-1.5">
                      <AlertCircle className="h-3 w-3 text-red-400 flex-shrink-0" />
                      <span>
                        <strong>#{r.line}</strong> {r.reason}
                        {r.value && <span className="text-gray-400"> · {r.value}</span>}
                        {r.email && <span className="text-gray-400"> · {r.email}</span>}
                      </span>
                    </div>
                  ))}
                  {importResult.country_duplicate?.slice(0, 5).map((r, i) => (
                    <div key={`cd-${i}`} className="flex items-center gap-1.5">
                      <Lock className="h-3 w-3 text-amber-500 flex-shrink-0" />
                      <span>
                        <strong>#{r.line}</strong> {r.country_en} ({r.country_code}) {tr ? 'zaten kayıtlı:' : 'already locked by:'} {r.existing || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* ── Liste ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">
              {tr ? 'Kayıtlı E-postalar' : 'Registered Emails'}<Badge n={emails.length} />
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input type="text" placeholder={tr ? 'E-posta, ad veya ülke...' : 'Email, name or country...'}
                value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-3 h-8 w-56 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300" />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-6 w-6 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              {search ? (tr ? 'Sonuç bulunamadı.' : 'No results found.') : (tr ? 'Henüz e-posta yok.' : 'No emails yet.')}
            </div>
          ) : (
            <ul className="divide-y divide-gray-50 max-h-[520px] overflow-y-auto">
              {filtered.map(e => {
                const invStatus = e.invitation?.status || 'not_sent';
                const isSending = sendingInvite[e.id];
                
                return (
                <li key={e.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 group transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${e.is_used ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                      {e.is_used
                        ? <Lock className="h-3.5 w-3.5 text-emerald-600" />
                        : <Mail className="h-3.5 w-3.5 text-blue-500" />
                      }
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-800 truncate">{e.email}</p>
                        {e.is_used && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex-shrink-0">
                            <CheckCircle2 className="h-2.5 w-2.5" />
                            {tr ? 'Profil Oluşturuldu' : 'Profile Created'}
                          </span>
                        )}
                        <InvitationBadge status={invStatus} tr={tr} />
                      </div>
                      <div className="flex items-center gap-2">
                        {e.name && <p className="text-xs text-gray-400 truncate">{e.name}</p>}
                        {(e.country_code || e.country) && (
                          <>
                            {e.name && <span className="text-gray-200 text-xs">·</span>}
                            <span className="inline-flex items-center gap-1 text-xs text-blue-500 font-medium">
                              <Globe className="h-2.5 w-2.5" />{displayCountry(e)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  {e.is_used ? (
                    <div title={tr ? 'Bu email ile profil oluşturulmuş, düzenlenemez/silinemez' : 'Profile created with this email, cannot edit/delete'}
                      className="ml-4 h-7 w-7 rounded-lg flex items-center justify-center text-emerald-300 flex-shrink-0">
                      <Lock className="h-3.5 w-3.5" />
                    </div>
                  ) : (
                    <div className="ml-4 flex items-center gap-1 flex-shrink-0">
                      {/* Invitation buttons */}
                      {(invStatus === 'not_sent' || invStatus === 'failed') && (
                        <button 
                          title={tr ? 'Davet Önizleme ve Gönder' : 'Preview and Send Invitation'}
                          onClick={() => handlePreviewEmail(e.id, e.email, e.country || e.country_code, e.name)}
                          disabled={isSending}
                          className="h-7 px-2 rounded-lg flex items-center justify-center gap-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors">
                          <Send className="h-3 w-3" />
                          {isSending ? '...' : (tr ? 'Gönder' : 'Send')}
                        </button>
                      )}
                      
                      {(invStatus === 'sent' || invStatus === 'expired') && (
                        <>
                          <button 
                            title={tr ? 'Yeniden Gönder' : 'Resend'}
                            onClick={() => handleResendInvitation(e.id, e.email)}
                            disabled={isSending}
                            className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors">
                            <RefreshCw className="h-3.5 w-3.5" />
                          </button>
                          <button 
                            title={tr ? 'Daveti Sil' : 'Delete Invitation'}
                            onClick={() => handleDeleteInvitation(e.id)}
                            className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 transition-colors">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                      
                      {/* Edit & Delete user buttons */}
                      <button title={tr ? 'Düzenle' : 'Edit'}
                        data-testid={`unfccc-edit-${e.id}`}
                        onClick={() => setEditTarget(e)}
                        className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button title={tr ? 'Sil' : 'Delete'}
                        data-testid={`unfccc-delete-${e.id}`}
                        onClick={() => setDeleteTarget({ id: e.id, email: e.email })}
                        className="h-7 w-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </li>
              )})}
            </ul>
          )}
        </div>
      </div>
      
      {/* Email Preview Modal */}
      {previewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {tr ? '📧 E-posta Önizleme' : '📧 Email Preview'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  <strong>{tr ? 'Alıcı:' : 'To:'}</strong> {previewModal.email}
                </p>
              </div>
              <button
                onClick={() => setPreviewModal(null)}
                className="h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Preview Content */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {previewLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="h-10 w-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <div 
                    dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                    className="prose prose-sm max-w-none"
                  />
                </div>
              )}
            </div>
            
            {/* Footer Actions */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                {tr 
                  ? '⏰ Bu davet linki 12 saat geçerlidir'
                  : '⏰ This invitation link is valid for 12 hours'}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewModal(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  {tr ? 'İptal' : 'Cancel'}
                </button>
                <button
                  onClick={handleConfirmSend}
                  disabled={previewLoading || sendingInvite[previewModal.email_id]}
                  className="px-6 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors">
                  <Send className="h-4 w-4" />
                  {sendingInvite[previewModal.email_id] 
                    ? (tr ? 'Gönderiliyor...' : 'Sending...') 
                    : (tr ? 'Onayla ve Gönder' : 'Confirm & Send')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
